import React, { useState } from 'react';
import { SearchIcon, BookIcon, ClockIcon, DollarSignIcon, FilterIcon } from 'lucide-react';
import BackButton from '../../components/UI/BackButton';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
const opportunities = [{
  id: 1,
  title: 'Developmental Editor for Fantasy Series',
  type: 'developmental',
  author: 'R.J. Mitchell',
  genre: 'Fantasy',
  deadline: '2 weeks',
  budget: '$2,000-$3,000',
  description: 'Seeking an experienced developmental editor for a 90,000-word epic fantasy novel, first in a trilogy. Looking for someone with strong world-building experience.',
  requirements: ['5+ years experience', 'Fantasy genre expertise', 'Series development experience'],
  status: 'Open',
  applicants: 12
}, {
  id: 2,
  title: 'Romance Novel Copy Editor',
  type: 'copyediting',
  author: 'Sarah Blake',
  genre: 'Romance',
  deadline: '1 month',
  budget: '$1,500-$2,000',
  description: 'Need a detail-oriented copy editor for a contemporary romance novel. Must be familiar with romance genre conventions and Chicago Manual of Style.',
  requirements: ['3+ years experience', 'Romance genre knowledge', 'Chicago Manual of Style proficiency'],
  status: 'Open',
  applicants: 8
}, {
  id: 3,
  title: 'Thriller Manuscript Proofreader',
  type: 'proofreading',
  author: 'Michael Chang',
  genre: 'Thriller',
  deadline: '3 weeks',
  budget: '$800-$1,200',
  description: 'Final proofread needed for a 70,000-word psychological thriller. Attention to detail and quick turnaround essential.',
  requirements: ['Proven proofreading experience', 'Fast turnaround', 'Thriller genre familiarity'],
  status: 'Urgent',
  applicants: 5
}];
const types = [{
  id: 'all',
  name: 'All Types'
}, {
  id: 'developmental',
  name: 'Developmental Editing'
}, {
  id: 'copyediting',
  name: 'Copy Editing'
}, {
  id: 'proofreading',
  name: 'Proofreading'
}];
const EditingOpportunities = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const filtered = opportunities.filter(o => {
    const matchesSearch = o.title.toLowerCase().includes(searchQuery.toLowerCase()) || o.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = selectedType === 'all' || o.type === selectedType;
    return matchesSearch && matchesType;
  });
  return <div className={`min-h-screen ${dk.page}`} data-id="element-13281" data-mp="41lj4k9pcneh">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-13282" data-mp="44zlvtma2ymg">
        <div className="mb-8" data-id="element-13283" data-mp="1kfbr423xzvte">
          <div className="mb-4" data-id="element-13284" data-mp="1kipqd1hb7owm">
            <BackButton data-id="element-13285" data-mp="1filbffw1v28i" />
          </div>
          <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-13286" data-mp="1fhk0dbf1afqs">
            Editing Opportunities
          </h1>
          <p className={`mt-2 text-lg ${dk.textSecondary}`} data-id="element-13287" data-mp="2xhro9l7fujdw">
            Find your next editing project
          </p>
        </div>

        {/* Search and Filter */}
        <div className="mb-8 flex flex-col md:flex-row gap-4" data-id="element-13288" data-mp="2og1uw9tzxadh">
          <div className="flex-1 relative" data-id="element-13289" data-mp="2tdbidjxtrbyo">
            <SearchIcon className={`h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 ${dk.textFaint}`} data-id="element-13290" data-mp="2ted4cjgdpiv6" />
            <input type="text" placeholder="Search opportunities..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.input}`} data-id="element-13291" data-mp="3l4zt620yfqfe" />
          </div>
          <div className="w-full md:w-64" data-id="element-13292" data-mp="3w45orqea5p8">
            <select value={selectedType} onChange={e => setSelectedType(e.target.value)} className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.input}`} data-id="element-13293" data-mp="8nm5vdklg295">
              {types.map(t => <option key={t.id} value={t.id} data-id="element-13294" data-mp="2baffg9c3znfc">
                  {t.name}
                </option>)}
            </select>
          </div>
        </div>

        {/* Opportunities */}
        <div className="space-y-6" data-id="element-13295" data-mp="229r1tfqmnp2z">
          {filtered.map(o => <div key={o.id} className={`rounded-lg shadow-md p-6 border ${dk.card}`} data-id="element-13296" data-mp="2yx5i9cwa7m10">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4" data-id="element-13297" data-mp="2uyfwavx6gw5r">
                <div data-id="element-13298" data-mp="2q30ksamn7cci">
                  <h2 className={`text-xl font-semibold ${dk.textPrimary}`} data-id="element-13299" data-mp="2l6il3p666471">
                    {o.title}
                  </h2>
                  <p className={dk.textSecondary} data-id="element-13300" data-mp="3impkg4hv5w02">{o.author}</p>
                </div>
                <span className={`mt-2 md:mt-0 inline-flex items-center px-2.5 py-0.5 ${rt.badgeRadius} text-xs font-medium ${o.status === 'Urgent' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`} data-id="element-13301" data-mp="3duo1jzq6x712">
                  {o.status}
                </span>
              </div>
              <p className={`mb-4 ${dk.textSecondary}`} data-id="element-13302" data-mp="3v3gyhbxctnrb">{o.description}</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4" data-id="element-13303" data-mp="30psejj6v80a">
                <div className="flex items-center gap-2" data-id="element-13304" data-mp="7zk8hdpruigp">
                  <BookIcon className={`h-4 w-4 ${dk.textFaint}`} data-id="element-13305" data-mp="7z10aopxb8fv" />
                  <span className={`text-sm ${dk.textSecondary}`} data-id="element-13306" data-mp="cxvhxnc5mqye">
                    {o.genre}
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-13307" data-mp="hrgk7rks673e">
                  <ClockIcon className={`h-4 w-4 ${dk.textFaint}`} data-id="element-13308" data-mp="mnf2cxfimsvd" />
                  <span className={`text-sm ${dk.textSecondary}`} data-id="element-13309" data-mp="qqbqf3ntrh6p">
                    {o.deadline}
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-13310" data-mp="vpwqxiin7xlj">
                  <DollarSignIcon className={`h-4 w-4 ${dk.textFaint}`} data-id="element-13311" data-mp="2rs028f9vr16z" />
                  <span className={`text-sm ${dk.textSecondary}`} data-id="element-13312" data-mp="2rpx395qr1xq4">
                    {o.budget}
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-13313" data-mp="31ick1zqb8g9f">
                  <FilterIcon className={`h-4 w-4 ${dk.textFaint}`} data-id="element-13314" data-mp="31jxjo3rhepe5" />
                  <span className={`text-sm ${dk.textSecondary}`} data-id="element-13315" data-mp="2wpt5p0jsth3k">
                    {types.find(t => t.id === o.type)?.name}
                  </span>
                </div>
              </div>
              <div className="mb-4" data-id="element-13316" data-mp="36gnuabkioss6">
                <h3 className={`text-sm font-medium mb-2 ${dk.textPrimary}`} data-id="element-13317" data-mp="3kgympj1h6ffg">
                  Requirements:
                </h3>
                <ul className="list-disc list-inside space-y-1" data-id="element-13318" data-mp="3fhm3d89tbqhm">
                  {o.requirements.map((req, i) => <li key={i} className={`text-sm ${dk.textSecondary}`} data-id="element-13319" data-mp="1q3pb9lpdcjuk" data-mpi={i}>
                      {req}
                    </li>)}
                </ul>
              </div>
              <div className="flex items-center justify-between" data-id="element-13320" data-mp="mts9usjac4y">
                <span className={`text-sm ${dk.textMuted}`} data-id="element-13321" data-mp="5klupiqt1gne">
                  {o.applicants} applicants
                </span>
                <button className={`px-4 py-2 rounded-md transition-colors text-sm font-medium text-white ${rt.primaryButton}`} data-id="element-13322" data-mp="3nxtf6xpvhofv">
                  Apply Now
                </button>
              </div>
            </div>)}
          {filtered.length === 0 && <div className={`text-center py-12 rounded-xl border-2 border-dashed ${dk.emptyState}`} data-id="element-13323" data-mp="3j0t5mctte3k0">
              <p className={`text-lg ${dk.textMuted}`} data-id="element-13324" data-mp="351tij9ly9wpy">
                No opportunities found matching your criteria.
              </p>
            </div>}
        </div>
      </div>
    </div>;
};
export default EditingOpportunities;